import axios, { AxiosInstance, isAxiosError } from 'axios';
import { API_CONFIG, API_PARAMS } from './config';
import { attachWorkspaceContext } from './workspaceContext';
import { installBanInterceptor } from './banInterceptor';
import { getIdToken } from '@/auth/firebase';
import { getToltReferral } from '@/services/toltService';
import { emailAttributionBody, isEmailOfferId, type EmailAttribution, type EmailOfferId } from '@/services/emailAttribution';
import { checkoutGoogleFields, checkoutMetaFields, checkoutMicrosoftFields, rememberMetaCheckout, signupAdAttribution, txnIdFromUrl } from '@/services/metaPixel';
import { 
  User, UserResponse, parseUser,
  Order, OrderResponse, parseOrder,
  Folder, FolderResponse, parseFolder,
  ReviewSubmission, RewardTaskType,
} from '@/types';
import type { CancelFlowInfo, AutoReloadSettings } from './v2Types';

/**
 * Thrown by `createHeygenChatSession` when the backend rejects a new plan
 * because the user is already at the concurrent-planning-session cap.
 * Consumers should detect via `instanceof TooManyActivePlansError` and route
 * the user to /files with a toast instead of a generic error state.
 */
export class TooManyActivePlansError extends Error {
  readonly code = 'too_many_active_plans' as const;
  readonly limit: number;
  constructor(limit: number) {
    super('too_many_active_plans');
    this.name = 'TooManyActivePlansError';
    this.limit = limit;
  }
}

/**
 * Hard ceiling we accept from a file picker. We could go higher technically
 * (S3 multipart supports up to 5 TB) but 5 GB is a sane limit for a HeyGen
 * asset — beyond that the worker's transcode would always trim hard anyway.
 */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;

/**
 * Thrown by `createCheckoutSession` when a recovery/exit offer checkout is
 * refused because the server-side deadline has passed (HTTP 410). The UI should
 * catch this, hide the offer, and tell the user it expired instead of surfacing
 * a generic checkout error.
 */
export class ExitOfferExpiredError extends Error {
  readonly code = 'offer_expired' as const;
  constructor() {
    super('offer_expired');
    this.name = 'ExitOfferExpiredError';
  }
}

// Anything at or above this size is uploaded with S3 multipart in parallel
// chunks. Below it, a single PUT is faster (no init/complete round-trips).
const MULTIPART_THRESHOLD_BYTES = 50 * 1024 * 1024;
const MULTIPART_PART_BYTES = 8 * 1024 * 1024;
const MULTIPART_PARALLEL_LANES = 6;

// Async HeyGen job polling parameters. The worker rarely takes more than a
// minute for files under a few hundred MB but we budget 5 min to cover 1+ GB
// transcodes plus HeyGen's own ingestion lag.
const HEYGEN_JOB_POLL_INTERVAL_MS = 2000;
const HEYGEN_JOB_POLL_MAX_MS = 5 * 60 * 1000;

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Attach the caller's Firebase ID token as a Bearer credential on every
    // backend request so the server can verify identity server-side (instead of
    // trusting the forgeable user_id in the body). Staged rollout: the backend
    // still accepts token-less requests until each endpoint is flipped to strict,
    // so this is purely additive and cannot break V1/V2. Best-effort — a missing
    // token (logged out, or the public /share page) just sends no header.
    this.client.interceptors.request.use(async (config) => {
      const token = await getIdToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Team workspaces: inject workspace_org_id on workspace-scoped endpoints
    // while a team workspace is active (personal workspace = no-op).
    this.client.interceptors.request.use(attachWorkspaceContext);

    // Block/ban: on 403 user_banned (from get-user-id at login/signup, or any
    // gated endpoint), flip the global ban screen and tear down the session.
    installBanInterceptor(this.client);

    // Add request/response interceptors for debugging
    if (import.meta.env.DEV) {
      this.client.interceptors.request.use((config) => {
        console.log('📤 API Request:', config.method?.toUpperCase(), config.url, config.data);
        return config;
      });

      this.client.interceptors.response.use(
        (response) => {
          console.log('📥 API Response:', response.status, response.data);
          return response;
        },
        (error) => {
          console.error('❌ API Error:', error.response?.status, error.response?.data);
          return Promise.reject(error);
        }
      );
    }
  }

  // ============ User Methods ============

  async getUserById(
    email: string,
    firebaseUid?: string,
    firstName?: string,
    lastName?: string,
    displayName?: string,
    profilePictureUrl?: string
  ): Promise<{ userId: string; created?: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.getUserById, {
      [API_PARAMS.userEmail]: email,
      [API_PARAMS.firebaseUid]: firebaseUid,
      [API_PARAMS.platform]: 'web',
      first_name: firstName,
      last_name: lastName,
      display_name: displayName,
      profile_picture_url: profilePictureUrl,
      // First-arrival fbclid / gclid / utm_*: stored write-once on the user (Meta / Google server fallback).
      ad_attribution: signupAdAttribution(),
    });

    // The lambda is get-OR-create: 201 (and no `exists` flag) means the Users
    // row was created by THIS call — the authoritative "new account" signal
    // (localStorage heuristics miscount returning users on fresh devices).
    const created = response.status === 201 || response.data?.exists === false;
    return { userId: response.data.user_id, created };
  }

  async getUserInfo(userId: string): Promise<User> {
    const response = await this.client.post<UserResponse>(
      API_CONFIG.endpoints.getUserInfo,
      { [API_PARAMS.userId]: userId }
    );

    return parseUser(response.data);
  }

  // ============ Passwordless email login (OTP) ============
  // Same email-otp lambda the mobile apps use: send a 6-digit code, verify it.
  // The store-reviewer account (vivideotestaccount@vivideo.ai, defined only in the
  // email-otp lambda) accepts its static code without an email being sent; every
  // earlier reviewer account was revoked on 2026-09-24. After verify succeeds, the caller
  // establishes the session via getUserById(email) (get-or-create).

  /** Send a login code to the email. Resolves on success; throws the backend
   *  error message (e.g. rate-limit) otherwise. */
  async sendEmailOtp(email: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.emailOtp, { action: 'send', email });
  }

  /** Verify the code. Resolves with the backend session token (the sign-in's
   *  Bearer credential); throws (401) on an invalid/expired code. */
  async verifyEmailOtp(email: string, code: string): Promise<string | null> {
    const response = await this.client.post(API_CONFIG.endpoints.emailOtp, { action: 'verify', email, code });
    return response.data?.session_token ?? null;
  }

  async updateUserInfo(
    userId: string,
    updates: Partial<{
      displayName: string;
      profilePictureUrl: string;
      fcmToken: string;
    }>
  ): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.updateUserInfo, {
      [API_PARAMS.userId]: userId,
      display_name: updates.displayName,
      profile_picture_url: updates.profilePictureUrl,
      fcm_token: updates.fcmToken,
    });
  }

  /**
   * Update FCM token for push notifications
   * Uses the set_fcm_token operation in the backend
   */
  async updateFcmToken(userId: string, fcmToken: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.updateUserInfo, {
      [API_PARAMS.userId]: userId,
      operation: 'set_fcm_token',
      value: fcmToken,
    });
  }

  // ============ Folder Methods ============

  async getUserFolders(userId: string): Promise<Folder[]> {
    const response = await this.client.post<{ folders: FolderResponse[]; count: number } | FolderResponse[]>(
      API_CONFIG.endpoints.getFolders,
      { [API_PARAMS.userId]: userId }
    );

    // Handle both { folders: [...] } and direct array format
    const folders = Array.isArray(response.data) 
      ? response.data 
      : (response.data?.folders || []);
    
    return folders.map(parseFolder);
  }

  async createFolder(folderName: string, userId: string): Promise<Folder> {
    const response = await this.client.post<FolderResponse>(
      API_CONFIG.endpoints.createFolder,
      {
        [API_PARAMS.folderName]: folderName,
        [API_PARAMS.userId]: userId,
      }
    );

    return parseFolder(response.data);
  }

  async renameFolder(folderId: string, newName: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.folderOperation, {
      [API_PARAMS.folderId]: folderId,
      [API_PARAMS.operation]: 'rename',
      [API_PARAMS.newName]: newName,
    });
  }

  async deleteFolder(folderId: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.folderOperation, {
      [API_PARAMS.folderId]: folderId,
      [API_PARAMS.operation]: 'delete',
    });
  }

  // ============ Order Methods ============

  // userId is required for team workspaces: get-files can only swap to the
  // org owner key after validating the CALLER's membership, and the caller is
  // identified by user_id in the body (folder_id alone isn't enough).
  async getUserOrders(folderId: string, userId?: string): Promise<Order[]> {
    const response = await this.client.post<{ files: OrderResponse[]; count: number } | OrderResponse[]>(
      API_CONFIG.endpoints.getFiles,
      {
        [API_PARAMS.folderId]: folderId,
        ...(userId ? { [API_PARAMS.userId]: userId } : {}),
      }
    );

    // Handle both { files: [...] } and direct array format
    const files = Array.isArray(response.data) 
      ? response.data 
      : (response.data?.files || []);
    
    return files.map(parseOrder);
  }

  async createOrder(
    userId: string,
    operationType: string,
    orderData: Record<string, unknown>,
    orderName?: string,
    folderId?: string
  ): Promise<{ 
    orderId: string; 
    isPreview?: boolean; 
    previewImageUrl?: string;
    status?: string;
  }> {
    // Use 65s timeout for createOrder since preview image generation can take up to 60s
    const response = await this.client.post(API_CONFIG.endpoints.createOrder, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderType]: operationType,
      [API_PARAMS.orderData]: orderData,
      [API_PARAMS.orderName]: orderName,
      [API_PARAMS.folderId]: folderId || userId,
      [API_PARAMS.platform]: 'web',
    }, {
      timeout: 65000, // 65s timeout (backend has 60s for fal.ai preview generation)
    });

    return { 
      orderId: response.data.order_id,
      isPreview: response.data.is_preview || response.data.status === 'preview',
      previewImageUrl: response.data.preview_image_url,
      status: response.data.status,
    };
  }

  async renameOrder(orderId: string, newName: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.fileOperation, {
      [API_PARAMS.orderId]: orderId,
      [API_PARAMS.operation]: 'rename',
      value: newName,
    });
  }

  async moveOrder(orderId: string, targetFolderId: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.fileOperation, {
      [API_PARAMS.orderId]: orderId,
      [API_PARAMS.operation]: 'move',
      value: targetFolderId,
    });
  }

  async deleteOrder(orderId: string): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.fileOperation, {
      [API_PARAMS.orderId]: orderId,
      [API_PARAMS.operation]: 'delete',
    });
  }

  // ============ File Upload Methods ============

  async getUploadUrl(
    userId: string,
    fileName: string,
    fileType: string
  ): Promise<{ uploadUrl: string; publicUrl: string }> {
    const response = await this.client.post(API_CONFIG.endpoints.fileUpload, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.fileName]: fileName,
      [API_PARAMS.fileType]: fileType,
    });

    return {
      uploadUrl: response.data.upload_url,
      publicUrl: response.data.public_url,
    };
  }

  async uploadFile(
    file: File,
    uploadUrl: string,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    // Use ArrayBuffer for more reliable uploads (matches mobile's approach).
    //
    // Two important rules for the PUT to a presigned S3 URL:
    //   1. Content-Type MUST exactly match the type the URL was minted with,
    //      otherwise S3 returns SignatureDoesNotMatch (403). Callers thread
    //      through the same value they passed to getUploadUrl.
    //   2. Don't set Content-Length manually — browsers refuse and axios warns
    //      ("Refused to set unsafe header Content-Length"). The browser fills
    //      it in from the body length automatically.
    const arrayBuffer = await file.arrayBuffer();

    await axios.put(uploadUrl, arrayBuffer, {
      headers: {
        'Content-Type': contentType,
      },
      maxRedirects: 5,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  /**
   * Multipart upload to S3. The webapp slices the file into 8 MB parts and
   * pushes 6 of them in parallel, which is roughly 4-6× faster than a single
   * PUT for users with fast connections, and also gives us per-part retry
   * granularity on flaky networks.
   *
   * Returns the public URL of the completed object. Aborts the upload on the
   * server side if any part permanently fails.
   */
  async uploadFileMultipart(
    userId: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<{ publicUrl: string; s3Key: string }> {
    const initRes = await this.client.post(API_CONFIG.endpoints.fileUpload, {
      action: 'multipart_init',
      [API_PARAMS.userId]: userId,
      [API_PARAMS.fileName]: file.name,
      [API_PARAMS.fileType]: contentType,
    });
    const uploadId: string = initRes.data.upload_id;
    const s3Key: string = initRes.data.s3_key;
    const publicUrl: string = initRes.data.public_url;

    const partSize = MULTIPART_PART_BYTES;
    const totalParts = Math.max(1, Math.ceil(file.size / partSize));
    // Per-part progress in bytes. Summed and divided by total to drive UX.
    const perPartLoaded = new Array<number>(totalParts).fill(0);
    const completedEtags = new Array<string | null>(totalParts).fill(null);

    const reportProgress = () => {
      if (!onProgress) return;
      const loaded = perPartLoaded.reduce((a, b) => a + b, 0);
      const pct = Math.min(100, Math.round((loaded * 100) / Math.max(1, file.size)));
      onProgress(pct);
    };

    const uploadOne = async (partIndex: number) => {
      const partNumber = partIndex + 1; // S3 parts are 1-indexed
      const start = partIndex * partSize;
      const end = Math.min(file.size, start + partSize);
      const slice = file.slice(start, end);

      const urlRes = await this.client.post(API_CONFIG.endpoints.fileUpload, {
        action: 'multipart_part_url',
        upload_id: uploadId,
        s3_key: s3Key,
        part_number: partNumber,
      });
      const partUrl: string = urlRes.data.upload_url;

      // Read the slice into an ArrayBuffer for the same reasons we do in the
      // single-PUT path: browsers handle ArrayBuffer bodies more predictably
      // than File/Blob across XHR implementations.
      const buf = await slice.arrayBuffer();
      const res = await axios.put(partUrl, buf, {
        headers: { 'Content-Type': contentType },
        maxRedirects: 5,
        onUploadProgress: (e) => {
          if (e.total) {
            perPartLoaded[partIndex] = e.loaded;
            reportProgress();
          }
        },
      });

      // S3 returns the part's ETag in the response header. We expose it via
      // CORS (ExposeHeaders includes ETag in the bucket config).
      const etag =
        (res.headers && (res.headers['etag'] || res.headers['ETag'])) || '';
      if (!etag) {
        throw new Error(`No ETag returned for part ${partNumber}`);
      }
      completedEtags[partIndex] = etag;
      // Snap this part's progress to its full size in case the final
      // onUploadProgress event was rounded down.
      perPartLoaded[partIndex] = end - start;
      reportProgress();
    };

    // Concurrency-limited fan-out. We pop part indexes off a shared queue so
    // every lane stays busy until the queue is empty, instead of waiting for
    // a strict batch barrier.
    const queue: number[] = [];
    for (let i = 0; i < totalParts; i++) queue.push(i);

    const worker = async () => {
      while (queue.length > 0) {
        const idx = queue.shift();
        if (idx === undefined) return;
        await uploadOne(idx);
      }
    };

    try {
      await Promise.all(
        Array.from(
          { length: Math.min(MULTIPART_PARALLEL_LANES, totalParts) },
          () => worker(),
        ),
      );
    } catch (err) {
      // Best-effort server-side cleanup so we don't leave orphan parts
      // accruing storage cost.
      try {
        await this.client.post(API_CONFIG.endpoints.fileUpload, {
          action: 'multipart_abort',
          upload_id: uploadId,
          s3_key: s3Key,
        });
      } catch {
        /* ignore — abort is best-effort */
      }
      throw err;
    }

    const parts = completedEtags.map((etag, i) => ({
      ETag: etag as string,
      PartNumber: i + 1,
    }));
    await this.client.post(API_CONFIG.endpoints.fileUpload, {
      action: 'multipart_complete',
      upload_id: uploadId,
      s3_key: s3Key,
      parts,
    });

    return { publicUrl, s3Key };
  }

  /**
   * Adaptive S3 upload: dispatches to multipart for large files, falls back
   * to a single PUT for small ones. Returns the public URL either way so
   * callers can hand it off to backend services that need to read it back.
   */
  private async uploadFileAdaptive(
    userId: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<{ publicUrl: string }> {
    if (file.size >= MULTIPART_THRESHOLD_BYTES) {
      const { publicUrl } = await this.uploadFileMultipart(userId, file, contentType, onProgress);
      return { publicUrl };
    }
    const { uploadUrl, publicUrl } = await this.getUploadUrl(userId, file.name, contentType);
    await this.uploadFile(file, uploadUrl, contentType, onProgress);
    return { publicUrl };
  }

  /**
   * Convert HEIC/HEIF file to JPEG using heic2any library (dynamically imported)
   */
  private async convertHeicToJpeg(file: File): Promise<File> {
    console.log(`🔄 Converting HEIC to JPEG: ${file.name} (${file.size} bytes)`);
    
    try {
      // Dynamic import to ensure proper loading of WebAssembly modules
      const heic2any = (await import('heic2any')).default;
      
      const blob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.85,
      });
      
      // heic2any can return array of blobs for multi-image HEIC, we take the first
      const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
      
      if (!jpegBlob || jpegBlob.size === 0) {
        throw new Error('Conversion produced empty result');
      }
      
      // Create new file with .jpg extension
      const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      const jpegFile = new File([jpegBlob], newFileName, { type: 'image/jpeg' });
      
      console.log(`✅ Converted ${file.name} (${file.size} bytes) → ${newFileName} (${jpegFile.size} bytes)`);
      return jpegFile;
    } catch (error) {
      console.error('heic2any conversion failed:', error);
      throw error;
    }
  }

  /**
   * Upload an image file with automatic format conversion for fal.ai compatibility.
   * - For supported formats (JPEG, PNG, WebP): uses presigned URL upload
   * - For unsupported formats (HEIC, HEIF): converts client-side to JPEG first
   */
  /**
   * Check if file is HEIC/HEIF format (by type or extension)
   */
  private isHeicFile(file: File, fileType: string): boolean {
    const typeCheck = fileType.toLowerCase().includes('heic') || fileType.toLowerCase().includes('heif');
    const extensionCheck = /\.(heic|heif)$/i.test(file.name);
    return typeCheck || extensionCheck;
  }

  /**
   * Convert file to base64 for backend conversion fallback
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/heic;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload HEIC via backend conversion (fallback when client-side fails)
   */
  private async uploadHeicViaBackend(
    userId: string,
    file: File,
    fileType: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    console.log(`📤 Uploading HEIC via backend conversion: ${file.name}`);
    onProgress?.(10);

    const base64Data = await this.fileToBase64(file);
    onProgress?.(30);

    const response = await this.client.post(API_CONFIG.endpoints.fileUpload, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.fileName]: file.name,
      [API_PARAMS.fileType]: fileType,
      file_data: base64Data,
    });

    onProgress?.(100);
    console.log(`✅ Image converted and uploaded via backend: ${response.data.public_url}`);
    
    return response.data.public_url;
  }

  async uploadImage(
    userId: string,
    file: File,
    fileType: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    // Check if HEIC/HEIF
    if (this.isHeicFile(file, fileType)) {
      console.log(`📤 HEIC detected: ${file.name} (type: ${fileType})`);
      onProgress?.(5);
      
      // Try client-side conversion first
      try {
        console.log(`🔄 Attempting client-side HEIC conversion...`);
        const convertedFile = await this.convertHeicToJpeg(file);
        console.log(`✅ Client-side conversion successful: ${convertedFile.name}`);
        onProgress?.(20);
        
        // Upload the converted JPEG
        const { uploadUrl, publicUrl } = await this.getUploadUrl(userId, convertedFile.name, 'image/jpeg');
        onProgress?.(30);

        await this.uploadFile(convertedFile, uploadUrl, 'image/jpeg', (progress) => {
          onProgress?.(30 + Math.round(progress * 0.7));
        });

        console.log(`✅ Image uploaded: ${publicUrl}`);
        return publicUrl;
      } catch (clientError) {
        console.warn('⚠️ Client-side HEIC conversion failed, falling back to backend:', clientError);
        
        // Fallback to backend conversion
        try {
          return await this.uploadHeicViaBackend(userId, file, fileType, onProgress);
        } catch (backendError) {
          console.error('❌ Backend HEIC conversion also failed:', backendError);
          throw new Error('Failed to convert HEIC image. Please convert to JPEG and try again.');
        }
      }
    }

    // For non-HEIC images, upload directly
    console.log(`📤 Uploading image: ${file.name} (${fileType})`);
    
    const { uploadUrl, publicUrl } = await this.getUploadUrl(userId, file.name, fileType);
    onProgress?.(20);

    await this.uploadFile(file, uploadUrl, fileType, (progress) => {
      onProgress?.(20 + Math.round(progress * 0.8));
    });

    console.log(`✅ Image uploaded: ${publicUrl}`);
    return publicUrl;
  }

  // ============ HeyGen Asset Upload ============

  /**
   * Upload any media file to HeyGen via 2-step process:
   * 1. Upload original file to S3 (via presigned URL)
   * 2. Call /upload-heygen-asset which downloads from S3, converts format if
   *    needed (e.g. HEIC->JPEG, MOV->MP4, WAV->MP3), and uploads to HeyGen.
   * Returns HeyGen asset_id for use with Video Agent.
   */
  async uploadHeygenAsset(
    userId: string,
    file: File,
    onProgress?: (progress: number) => void,
    // Which backend lambda to forward to. Defaults to the V1 endpoint so V1 callers
    // are untouched; V2 passes v2UploadHeygenAsset (adds PDF support).
    endpoint: string = API_CONFIG.endpoints.uploadHeygenAsset,
  ): Promise<{ assetId: string; url: string; fileType: string; publicUrl: string }> {
    // Step 1: Upload to S3 (0% -> 60%). Multipart in parallel for big files.
    onProgress?.(2);
    const contentType = file.type || 'application/octet-stream';
    const { publicUrl } = await this.uploadFileAdaptive(
      userId,
      file,
      contentType,
      (progress) => onProgress?.(2 + Math.round(progress * 0.58)),
    );

    // Step 2: Submit to backend (60% -> 65%)
    onProgress?.(62);
    const submitRes = await this.client.post(
      endpoint,
      {
        action: 'submit',
        user_id: userId,
        s3_url: publicUrl,
        content_type: contentType,
      },
      {
        // Submit itself returns fast — images are sync (a few seconds), and
        // video/audio bounce out near-instantly with a job_id. We don't need
        // a 5-minute axios timeout for the submit call anymore.
        timeout: 60000,
      }
    );

    // Image fast path: backend already did the work synchronously.
    if (submitRes.data?.asset_id) {
      onProgress?.(100);
      return {
        assetId: submitRes.data.asset_id,
        url: submitRes.data.url,
        fileType: submitRes.data.file_type,
        publicUrl,
      };
    }

    // Video / audio: backend returned a job, poll for completion.
    const jobId: string | undefined = submitRes.data?.job_id;
    if (!jobId) {
      throw new Error('Server did not return asset_id or job_id');
    }

    const result = await this.pollHeygenJob(jobId, (pct) => {
      // Map worker progress (0-100) onto our 65-99 band so the UI keeps
      // moving while the backend transcodes.
      onProgress?.(65 + Math.round(pct * 0.34));
    }, endpoint);

    onProgress?.(100);
    return { ...result, publicUrl };
  }

  /**
   * Poll the upload-heygen-asset endpoint with action=status until the job
   * row reports done or failed. Animates a fake progress bar so the user
   * can see something is happening even while we have no real progress
   * signal from the worker (it's a single ffmpeg run).
   */
  private async pollHeygenJob(
    jobId: string,
    onAnimatedProgress?: (pct: number) => void,
    endpoint: string = API_CONFIG.endpoints.uploadHeygenAsset,
  ): Promise<{ assetId: string; url: string; fileType: string }> {
    const startedAt = Date.now();

    // Animate 0 -> 95 over ~2 minutes so the bar feels alive even though
    // we have no granular signal from the worker. The final jump to 100
    // happens when polling resolves successfully.
    let animatedPct = 0;
    const animationInterval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const target = Math.min(95, Math.round((elapsed / 120000) * 95));
      if (target > animatedPct) {
        animatedPct = target;
        onAnimatedProgress?.(animatedPct);
      }
    }, 500);

    try {
      while (Date.now() - startedAt < HEYGEN_JOB_POLL_MAX_MS) {
        await new Promise((resolve) => setTimeout(resolve, HEYGEN_JOB_POLL_INTERVAL_MS));
        const res = await this.client.post(
          endpoint,
          { action: 'status', job_id: jobId },
          { timeout: 15000 },
        );
        const data = res.data || {};
        if (data.status === 'done') {
          if (!data.asset_id) {
            throw new Error('Job marked done but no asset_id was returned');
          }
          onAnimatedProgress?.(100);
          return {
            assetId: data.asset_id,
            url: data.url || '',
            fileType: data.file_type || '',
          };
        }
        if (data.status === 'failed') {
          throw new Error(data.error || 'Upload failed');
        }
        // pending / processing — keep polling
      }
      throw new Error('Upload timed out — please retry');
    } finally {
      clearInterval(animationInterval);
    }
  }

  async uploadHeygenAssetByUrl(
    userId: string,
    videoUrl: string
  ): Promise<{ assetId: string }> {
    const response = await this.client.post(
      API_CONFIG.endpoints.uploadHeygenAsset,
      { action: 'submit', user_id: userId, s3_url: videoUrl, content_type: 'video/mp4' },
      { timeout: 60000 }
    );
    if (response.data?.asset_id) {
      return { assetId: response.data.asset_id };
    }
    const jobId: string | undefined = response.data?.job_id;
    if (!jobId) {
      throw new Error('Server did not return asset_id or job_id');
    }
    const result = await this.pollHeygenJob(jobId);
    return { assetId: result.assetId };
  }

  // ============ Account Deletion ============

  /** Delete the account. Email-OTP sessions have no Firebase token, so for them
   *  delete-user requires a fresh emailed code as the ownership proof. Pass the
   *  raw code — the backend verifies AND burns it (single-use); verifying it
   *  client-side first would consume it and make the deletion fail. */
  async deleteUser(userId: string, otpCode?: string): Promise<{ success: boolean; deletedOrders: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.deleteUser, {
      [API_PARAMS.userId]: userId,
      ...(otpCode ? { otp_code: otpCode } : {}),
    });

    return {
      success: response.data.success,
      deletedOrders: response.data.deleted_orders || 0,
    };
  }

  // ============ Stripe Methods ============

  async createCheckoutSession(
    userId: string,
    priceType: 'weekly' | 'monthly' | 'yearly' = 'weekly',
    customerEmail?: string,
    promotionCode?: string,
    successUrl?: string,
    cancelUrl?: string,
    skipDiscount?: boolean,
    priceId?: string,
    couponId?: string,
    trialPeriodDays?: number,
    checkoutMode?: 'subscription' | 'trial_setup' | 'update_card',
    uiMode?: 'embedded',
    // Server-validated recovery offer. When set, the backend ignores price_id /
    // coupon_id, forces Pro weekly, and only grants the $4.99 first week if the
    // user's exit-offer deadline (set by v2-exit-offer) is still live — otherwise
    // it responds 410 and this method throws ExitOfferExpiredError.
    // email_699 / email_499 / email_199: recovery-email offers, validated against Users.email_offer.
    offer?: 'exit_first_week' | EmailOfferId,
    // Recovery-email identity → email_* fields, stored by the backend in Stripe metadata.
    emailAttribution?: EmailAttribution | null
  ): Promise<{ checkoutUrl?: string; clientSecret?: string }> {
    const returnUrl = uiMode === 'embedded'
      ? `${window.location.origin}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}`
      : undefined;
    // The txn_id in the success URL is the Meta event id shared with the server CAPI Purchase.
    const metaEventId = (checkoutMode || 'subscription') === 'subscription' ? txnIdFromUrl(successUrl) : null;
    try {
      const response = await this.client.post(API_CONFIG.endpoints.createCheckoutSession, {
        [API_PARAMS.userId]: userId,
        mode: checkoutMode || 'subscription',
        price_type: priceType,
        customer_email: customerEmail,
        promotion_code: promotionCode || undefined,
        success_url: uiMode === 'embedded' ? undefined : (successUrl || `${window.location.origin}/subscription?success=true`),
        cancel_url: uiMode === 'embedded' ? undefined : (cancelUrl || `${window.location.origin}/subscription?canceled=true`),
        skip_discount: skipDiscount || false,
        price_id: priceId || undefined,
        coupon_id: couponId || undefined,
        trial_period_days: trialPeriodDays || undefined,
        ui_mode: uiMode || undefined,
        return_url: returnUrl || undefined,
        offer: offer || undefined,
        tolt_referral: getToltReferral() || undefined,
        ...emailAttributionBody(emailAttribution),
        ...(metaEventId ? checkoutMetaFields(metaEventId) : {}),
        // Google Ads click ids for stripe-webhook's server-side first-subscription conversion.
        ...((checkoutMode || 'subscription') === 'subscription' ? checkoutGoogleFields() : {}),
        // Microsoft Ads click id for stripe-webhook's server-side offline conversion.
        ...((checkoutMode || 'subscription') === 'subscription' ? checkoutMicrosoftFields() : {}),
      });

      if (metaEventId) {
        const d = response.data || {};
        rememberMetaCheckout(metaEventId, {
          first: typeof d.first_subscription === 'boolean' ? d.first_subscription : undefined,
          value: typeof d.amount_total === 'number' ? d.amount_total / 100 : undefined,
          currency: typeof d.currency === 'string' ? d.currency.toUpperCase() : undefined,
        });
      }

      if (uiMode === 'embedded') {
        return { clientSecret: response.data.client_secret };
      }
      return { checkoutUrl: response.data.checkout_url };
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 410) {
        throw new ExitOfferExpiredError();
      }
      throw err;
    }
  }

  /**
   * Trade a recovery-email login ticket (the `lt` query param) for a real
   * session token. Returns null when the ticket is invalid/expired so the caller
   * can fall back to the normal login page.
   */
  async emailLoginExchange(
    ticket: string
  ): Promise<{ sessionToken: string; email: string; userId: string } | null> {
    try {
      const response = await this.client.post(API_CONFIG.endpoints.emailLoginExchange, { ticket });
      const d = response.data || {};
      if (!d.session_token) return null;
      return { sessionToken: d.session_token, email: d.email, userId: d.user_id };
    } catch {
      return null;
    }
  }

  /**
   * Ask the server to START the one-time exit offer (fires when a free user
   * leaves the paywall without ever starting a checkout). The 10-minute deadline
   * lives on the server, so `expires_at` is the source of truth for the timer.
   * `action: 'status'` is read-only and used to restore the banner after a reload.
   */
  async exitOffer(
    userId: string,
    action: 'start' | 'status' = 'start'
  ): Promise<{ eligible: boolean; expiresAt: number; reason?: string }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2ExitOffer, {
      [API_PARAMS.userId]: userId,
      action,
    });
    const d = response.data || {};
    return {
      eligible: !!d.eligible,
      expiresAt: Number(d.expires_at || 0),
      reason: d.reason,
    };
  }

  /**
   * Status of the user's recovery-email offer (set when an offer email was sent).
   * `expiresAt` is unix ms (0 when absent); the checkout re-validates the deadline.
   */
  async emailOfferStatus(userId: string): Promise<{
    active: boolean;
    offer: EmailOfferId | null;
    priceCents: number;
    expiresAt: number;
  }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2EmailOffer, {
      [API_PARAMS.userId]: userId,
      action: 'status',
    });
    const d = response.data || {};
    const expiresAt = d.expires_at ? Date.parse(d.expires_at) : 0;
    return {
      active: !!d.active,
      offer: isEmailOfferId(d.offer) ? d.offer : null,
      priceCents: Number(d.price_cents) || 0,
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : 0,
    };
  }

  async activateTrial(
    checkoutSessionId: string,
    userId: string
  ): Promise<{ success: boolean; alreadyActivated?: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.activateTrial, {
      checkout_session_id: checkoutSessionId,
      [API_PARAMS.userId]: userId,
    });
    return {
      success: true,
      alreadyActivated: response.data.already_activated || false,
    };
  }

  async getPaymentInfo(userId: string): Promise<{
    hasCard: boolean;
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
  }> {
    const response = await this.client.post(API_CONFIG.endpoints.directPayment, {
      [API_PARAMS.userId]: userId,
      action: 'get_payment_info',
    });
    return response.data;
  }

  async previewUpgrade(userId: string, priceId: string): Promise<{ amountDue: number; currency: string; isTrialUpgrade: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.directPayment, {
      [API_PARAMS.userId]: userId,
      action: 'preview_upgrade',
      price_id: priceId,
    });
    return { amountDue: response.data.amountDue, currency: response.data.currency, isTrialUpgrade: !!response.data.isTrialUpgrade };
  }

  async directUpgradePlan(userId: string, priceId: string): Promise<{ success: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.directPayment, {
      [API_PARAMS.userId]: userId,
      action: 'upgrade_plan',
      price_id: priceId,
    });
    return { success: response.data.success };
  }

  async directBuyCredits(userId: string, creditAmount: number): Promise<{ success: boolean; amountCharged?: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.directPayment, {
      [API_PARAMS.userId]: userId,
      action: 'buy_credits',
      credit_amount: creditAmount,
    });
    return { success: response.data.success, amountCharged: response.data.amountCharged };
  }

  /** After a setup-mode "update_card" Checkout, adopt the new PM as default. */
  async adoptPaymentMethod(userId: string, checkoutSessionId: string): Promise<{ success: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.directPayment, {
      [API_PARAMS.userId]: userId,
      action: 'adopt_payment_method',
      checkout_session_id: checkoutSessionId,
    });
    return { success: response.data.success };
  }

  async manageSubscription(
    userId: string,
    action: 'pause' | 'free_week' | 'yearly_bonus'
  ): Promise<{ success: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.manageSubscription, {
      [API_PARAMS.userId]: userId,
      action,
    });
    return { success: response.data.success };
  }

  // ===== Auto-reload credits (paid users) =====
  // Settings live on the Users row; `check` is server-authoritative (lock,
  // cooldown, daily cap, auto-disable after failures) — the client only nudges.

  async autoReloadGet(userId: string): Promise<AutoReloadSettings> {
    const response = await this.client.post(API_CONFIG.endpoints.v2AutoReload, {
      [API_PARAMS.userId]: userId,
      action: 'get',
    });
    return response.data as AutoReloadSettings;
  }

  async autoReloadSet(
    userId: string,
    settings: { enabled: boolean; threshold: number; amount: number }
  ): Promise<{ enabled: boolean; threshold: number; amount: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2AutoReload, {
      [API_PARAMS.userId]: userId,
      action: 'set',
      enabled: settings.enabled,
      threshold: settings.threshold,
      amount: settings.amount,
    });
    return response.data;
  }

  /** Nudge the server to evaluate a below-threshold top-up. Never throws. */
  async autoReloadCheck(userId: string): Promise<{ status: string; creditsAdded?: number }> {
    try {
      const response = await this.client.post(API_CONFIG.endpoints.v2AutoReload, {
        [API_PARAMS.userId]: userId,
        action: 'check',
      });
      return response.data;
    } catch {
      return { status: 'error' };
    }
  }

  // ===== V2 cancellation funnel (Settings → Manage Subscription) =====
  // One lambda, four actions. `info` powers every screen of the funnel so the
  // numbers shown (price, renewal date, credits, coupon) always come from
  // Stripe/DynamoDB — never from guesses in the client.

  async cancelFlowInfo(userId: string): Promise<CancelFlowInfo> {
    const response = await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'info',
    });
    return response.data as CancelFlowInfo;
  }

  /** Fire-and-forget funnel analytics (which step, which reason, where they dropped). */
  async cancelFlowSurvey(
    userId: string,
    payload: { flowId: string; step: string; reason?: string | null; details?: string; outcome?: string }
  ): Promise<void> {
    await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'survey',
      flow_id: payload.flowId,
      step: payload.step,
      reason: payload.reason || undefined,
      details: payload.details || undefined,
      outcome: payload.outcome || undefined,
    });
  }

  /**
   * Opens Stripe's hosted billing page (change card, billing info, invoices).
   * Uses a portal configuration with cancellation DISABLED — cancelling always
   * goes through the in-app funnel, never this page.
   */
  async cancelFlowBillingPortal(userId: string, returnUrl: string): Promise<{ portalUrl: string }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'billing_portal',
      return_url: returnUrl,
    });
    return { portalUrl: response.data.portalUrl };
  }

  /** Pauses billing for 1 week (Stripe pause_collection; credits return with the next payment). */
  async cancelFlowPause(userId: string, flowId: string): Promise<{ resumesAt: number; days: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'pause',
      flow_id: flowId,
    });
    return { resumesAt: response.data.resumesAt, days: response.data.days };
  }

  /** Adds the one-time 50 stay-with-us bonus credits (atomic, once per user ever). */
  async cancelFlowClaimBonus(userId: string, flowId: string): Promise<{ creditsAdded: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'bonus',
      flow_id: flowId,
    });
    return { creditsAdded: response.data.creditsAdded };
  }

  /**
   * Final step. Returns a Stripe hosted cancel page to redirect to
   * (`portalUrl`), or — when the billing portal has no cancel flow — reports
   * the subscription as already cancelled at `periodEnd`.
   */
  async cancelFlowCancel(
    userId: string,
    payload: { flowId: string; reason: string; details?: string; returnUrl: string }
  ): Promise<{ portalUrl?: string; canceled?: boolean; periodEnd?: number }> {
    const response = await this.client.post(API_CONFIG.endpoints.v2CancelSubscription, {
      [API_PARAMS.userId]: userId,
      action: 'cancel',
      flow_id: payload.flowId,
      reason: payload.reason,
      details: payload.details || undefined,
      return_url: payload.returnUrl,
    });
    return {
      portalUrl: response.data.portalUrl,
      canceled: response.data.canceled,
      periodEnd: response.data.periodEnd,
    };
  }

  async createCreditPurchaseSession(
    userId: string,
    creditAmount: number,
    customerEmail?: string,
    successUrl?: string,
    cancelUrl?: string,
    uiMode?: 'embedded'
  ): Promise<{ checkoutUrl?: string; clientSecret?: string }> {
    const returnUrl = uiMode === 'embedded'
      ? `${window.location.origin}/subscription?success=true&credits=${creditAmount}&session_id={CHECKOUT_SESSION_ID}`
      : undefined;
    const response = await this.client.post(API_CONFIG.endpoints.createCheckoutSession, {
      [API_PARAMS.userId]: userId,
      mode: 'credits',
      credit_amount: creditAmount,
      customer_email: customerEmail,
      success_url: uiMode === 'embedded' ? undefined : (successUrl || `${window.location.origin}/subscription?success=true&credits=${creditAmount}`),
      cancel_url: uiMode === 'embedded' ? undefined : (cancelUrl || `${window.location.origin}/subscription?canceled=true`),
      ui_mode: uiMode || undefined,
      return_url: returnUrl || undefined,
      tolt_referral: getToltReferral() || undefined,
    });

    if (uiMode === 'embedded') {
      return { clientSecret: response.data.client_secret };
    }
    return { checkoutUrl: response.data.checkout_url };
  }

  // ============ HeyGen v3 Chat Methods (v2 UI) ============
  // These are additive — they do NOT touch the v1 HeyGen flow (/create-order with
  // video_provider=heygen). The callback URL is also separate so the two flows
  // never overlap during the v1/v2 A/B test.

  async createHeygenChatSession(
    userId: string,
    prompt: string,
    options?: {
      orientation?: 'landscape' | 'portrait';
      files?: Array<Record<string, unknown>>;
      folderId?: string;
    }
  ): Promise<{ orderId: string; sessionId: string; status: string; orderName: string; videoId?: string }> {
    try {
      const response = await this.client.post(API_CONFIG.endpoints.heygenChatCreate, {
        [API_PARAMS.userId]: userId,
        prompt,
        orientation: options?.orientation,
        files: options?.files,
        [API_PARAMS.folderId]: options?.folderId,
      }, {
        timeout: 60000,
      });

      return {
        orderId: response.data.order_id,
        sessionId: response.data.session_id,
        status: response.data.status,
        orderName: response.data.order_name,
        videoId: response.data.video_id,
      };
    } catch (err) {
      // Unwrap the backend's structured 409 so PlanModal can route the user
      // to /files with a friendly toast instead of a generic error state.
      const axErr = err as {
        response?: { status?: number; data?: { error?: string; limit?: number } };
      };
      if (
        axErr?.response?.status === 409 &&
        axErr.response.data?.error === 'too_many_active_plans'
      ) {
        throw new TooManyActivePlansError(axErr.response.data.limit ?? 3);
      }
      throw err;
    }
  }

  async sendHeygenChatMessage(
    userId: string,
    orderId: string,
    message: string,
    options?: {
      autoProceed?: boolean;
      files?: Array<Record<string, unknown>>;
      avatarId?: string;
      voiceId?: string;
      durationSec?: number;
    }
  ): Promise<{
    runId?: string;
    title?: string;
    status?: string;
    autoProceed: boolean;
    creditsCharged: number;
    durationSec?: number;
  }> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatMessage, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
      message,
      auto_proceed: options?.autoProceed || false,
      files: options?.files,
      avatar_id: options?.avatarId,
      voice_id: options?.voiceId,
      duration_sec: options?.durationSec,
    }, {
      timeout: 60000,
    });

    return {
      runId: response.data.run_id,
      title: response.data.title,
      status: response.data.status,
      autoProceed: !!response.data.auto_proceed,
      creditsCharged: response.data.credits_charged || 0,
      durationSec: response.data.duration_sec,
    };
  }

  async getHeygenChatSession(
    userId: string,
    orderId: string
  ): Promise<{
    local: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    heygen: Record<string, any>;
  }> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatSession, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
    });
    return {
      local: response.data.local || {},
      heygen: response.data.heygen || {},
    };
  }

  async getHeygenChatResource(
    userId: string,
    orderId: string,
    resourceId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<Record<string, any>> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatResource, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
      resource_id: resourceId,
    });
    return response.data.resource || {};
  }

  async stopHeygenChatSession(
    userId: string,
    orderId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<Record<string, any>> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatStop, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
    });
    return response.data.result || {};
  }

  /**
   * Halt the current HeyGen agent run WITHOUT terminating the chat session.
   * Sends keep_alive=true to the same /heygen-chat-stop endpoint, which calls
   * HeyGen's /v3/video-agents/{session_id}/stop (halts at next checkpoint and
   * preserves partial results). Used as an approval-gate safety net: when a
   * plan first arrives we halt the agent so it cannot auto-proceed into
   * generation while the user is still reviewing.
   */
  async haltHeygenChatSession(
    userId: string,
    orderId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<Record<string, any>> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatStop, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
      keep_alive: true,
    });
    return response.data.result || {};
  }

  /**
   * Discard a plan session: halts HeyGen AND deletes the local Orders row
   * so the session never appears in /files. Used when the user closes the
   * PlanModal before approving — the plan is thrown away and any credits
   * that might have been pre-charged are refunded server-side.
   */
  async discardHeygenChatSession(
    userId: string,
    orderId: string
  ): Promise<{ deleted: boolean }> {
    const response = await this.client.post(API_CONFIG.endpoints.heygenChatStop, {
      [API_PARAMS.userId]: userId,
      [API_PARAMS.orderId]: orderId,
      delete: true,
    });
    return { deleted: !!response.data.deleted };
  }

  // ============ Rewards / Review Submissions ============

  /**
   * Step 1 of the review screenshot flow. Asks the backend for a presigned
   * S3 PUT URL into the dedicated review screenshots bucket. The backend
   * also assigns a `submission_id` we can use to finalize the row after
   * the upload succeeds.
   */
  async getReviewUploadUrl(
    userId: string,
    taskType: RewardTaskType,
    fileName: string,
    fileType: string,
  ): Promise<{ submissionId: string; uploadUrl: string; s3Key: string }> {
    const response = await this.client.post(API_CONFIG.endpoints.getReviewUploadUrl, {
      [API_PARAMS.userId]: userId,
      task_type: taskType,
      [API_PARAMS.fileName]: fileName,
      [API_PARAMS.fileType]: fileType,
    });
    return {
      submissionId: response.data.submission_id,
      uploadUrl: response.data.upload_url,
      s3Key: response.data.s3_key,
    };
  }

  /**
   * Step 2: after the screenshot has been PUT to S3, ping the backend to
   * persist a pending row in ReviewSubmissions so the support team can
   * later approve or reject it. proof_url is required (anti-abuse).
   */
  async submitReview(
    userId: string,
    submissionId: string,
    taskType: RewardTaskType,
    s3Key: string,
    proofUrl: string,
  ): Promise<{ success: boolean; status: string; rewardCredits: number; createdAt: string }> {
    const response = await this.client.post(API_CONFIG.endpoints.submitReview, {
      [API_PARAMS.userId]: userId,
      submission_id: submissionId,
      task_type: taskType,
      s3_key: s3Key,
      proof_url: proofUrl,
    });
    return {
      success: !!response.data.success,
      status: response.data.status,
      rewardCredits: response.data.reward_credits,
      createdAt: response.data.created_at,
    };
  }

  async listUserSubmissions(userId: string): Promise<ReviewSubmission[]> {
    const response = await this.client.post<{ submissions: ReviewSubmission[] }>(
      API_CONFIG.endpoints.listUserSubmissions,
      { [API_PARAMS.userId]: userId }
    );
    return response.data.submissions || [];
  }

  /**
   * Submit an enterprise sales inquiry from the pricing page. Backend forwards
   * the form contents to support@vivideo.ai via Resend (with Reply-To set to the
   * submitter's email so admin can hit Reply directly).
   */
  async submitEnterpriseContact(payload: {
    name: string;
    email: string;
    monthlyVideoCount: '1-99' | '100-999' | '1000+';
    message: string;
    source?: string;
  }): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success?: boolean; error?: string }>(
      API_CONFIG.endpoints.enterpriseContact,
      {
        name: payload.name,
        email: payload.email,
        monthly_video_count: payload.monthlyVideoCount,
        message: payload.message,
        source: payload.source || 'webapp',
      }
    );
    return { success: !!response.data.success };
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;

